import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Map as MapIcon, Activity, UserPlus, Search, Ban, Trash2, ShieldCheck, Euro, CreditCard, RefreshCw, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { confirmDialog } from "../components/ConfirmHost";
import { useAuth } from "../context/AuthContext";
import { FeedbackAdmin } from "../components/admin/FeedbackAdmin";
import { ChargerReportsAdmin } from "../components/admin/ChargerReportsAdmin";
import { ServerStatusAdmin } from "../components/admin/ServerStatusAdmin";
import { UpdatesAdmin } from "../components/admin/UpdatesAdmin";
import { IdeasAdmin } from "../components/admin/IdeasAdmin";
import { AnalyticsAdmin } from "../components/admin/AnalyticsAdmin";
import { GuestsAdmin } from "../components/admin/GuestsAdmin";
import { DrillDialog } from "../components/admin/DrillDialog";
import { Navbar } from "../components/Navbar";
import { Input } from "../components/ui/input";

const StatCard = ({ icon: Icon, label, value, sub, onClick, testId }) => (
  <button type="button" onClick={onClick} disabled={!onClick} title={onClick ? "Klik voor details" : undefined} data-testid={testId}
    className={`bg-card rounded-2xl p-5 flex items-center gap-4 text-left w-full ${onClick ? "cursor-pointer hover:ring-2 hover:ring-primary/30 active:scale-[0.99] transition-[box-shadow,transform] duration-200" : "cursor-default"}`}>
    <span className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-primary" />
    </span>
    <div>
      <p className="font-mono font-extrabold text-2xl leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}{sub ? ` · ${sub}` : ""}</p>
    </div>
  </button>
);

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [billing, setBilling] = useState(null);
  const [q, setQ] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [drill, setDrill] = useState(null);
  const [version, setVersion] = useState(null);

  useEffect(() => { api.get("/version").then((r) => setVersion(r.data.version)).catch(() => {}); }, []);

  const restoreVero = async () => {
    setRestoring(true);
    try {
      const r = await api.post("/admin/restore-vero");
      const d = r.data;
      toast.success(`Hersteld in '${d.trip}': ${d.entries} momenten, ${d.photos} foto's, ${d.comments} reacties, ${d.reactions} emoji's, ${d.extra_costs} extra kosten`, { duration: 10000 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRestoring(false);
    }
  };

  const load = useCallback(async (query = "") => {
    try {
      const [s, u, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: query ? { q: query } : {} }),
        api.get("/admin/payments"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setBilling(b.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/dashboard");
    }
  }, [navigate]);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/dashboard"); return; }
    if (user) load();
  }, [user, load, navigate]);

  useEffect(() => {
    const t = setTimeout(() => { if (users !== null) load(q); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const update = async (u, patch) => {
    try {
      await api.patch(`/admin/users/${u.id}`, patch);
      toast.success(`${u.email} bijgewerkt`);
      load(q);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!(await confirmDialog(`${u.email} en al diens trips definitief verwijderen?`, { title: "Gebruiker verwijderen", confirmLabel: "Definitief verwijderen", destructive: true }))) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Gebruiker verwijderd");
      load(q);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-2">Beheer</p>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-8">Admin</h1>

        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard icon={Users} label="Gebruikers" value={stats.total_users} testId="stat-users" onClick={() => setDrill("users")} />
            <StatCard icon={UserPlus} label="Nieuw (7 dagen)" value={stats.new_users_7d} testId="stat-new-users" onClick={() => setDrill("new_users")} />
            <StatCard icon={MapIcon} label="Trips" value={stats.total_trips} sub={`${stats.active_trips} live`} testId="stat-trips" onClick={() => setDrill("trips")} />
            <StatCard icon={Activity} label="Abonnementen" value={`${stats.plans.pro + stats.plans.family}`} sub={`${stats.plans.pro} Pro · ${stats.plans.family} Family`} testId="stat-subs" onClick={() => setDrill("subs")} />
          </div>
        )}
        <DrillDialog metric={drill} onClose={() => setDrill(null)} />

        <ServerStatusAdmin />

        <AnalyticsAdmin />

        {billing && (
          <div data-testid="admin-payments-section" className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" /> Betalingen</h2>
              <button data-testid="admin-payments-refresh" onClick={() => load(q)} title="Vernieuwen"
                className="p-2 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
              <StatCard icon={Euro} label="Omzet (betaald)" value={`€${billing.summary.paid_total.toFixed(2)}`} />
              <StatCard icon={Activity} label="Betaalde transacties" value={billing.summary.paid_count} />
              <StatCard icon={CreditCard} label="Open betalingen" value={billing.summary.open_count} />
            </div>
            <div data-testid="admin-payments-table" className="space-y-2">
              {billing.payments.slice(0, 25).map((p) => {
                const st = { paid: "bg-emerald-100 text-emerald-700", open: "bg-amber-100 text-amber-700" }[p.status] || "bg-red-100 text-red-600";
                return (
                  <div key={p.id} data-testid={`admin-payment-row-${p.mollie_payment_id}`} className="bg-card rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{p.name || "—"} <span className="text-muted-foreground font-normal">· {p.email}</span></p>
                      <p className="text-xs text-muted-foreground font-mono truncate">{p.created_at?.slice(0, 16).replace("T", " ")} · {p.mollie_payment_id}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-secondary rounded-full px-2.5 py-1">{p.plan}</span>
                      {p.discount && <span className="text-[10px] font-bold font-mono bg-[#B5838D]/15 text-[#8E5D67] rounded-full px-2.5 py-1">{p.discount}</span>}
                      <span className="font-mono font-bold text-sm">€{(p.amount ?? 0).toFixed(2)}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 ${st}`}>{p.status}</span>
                      {p.has_subscription && <span className="text-[10px] font-bold uppercase tracking-wide bg-primary/10 text-primary rounded-full px-2.5 py-1" title="Actief abonnement">abo</span>}
                    </div>
                  </div>
                );
              })}
              {billing.payments.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center bg-card rounded-2xl">Nog geen betalingen — deel je lanceeractie! 🚀</p>}
            </div>
          </div>
        )}

        <FeedbackAdmin />

        <ChargerReportsAdmin />

        <IdeasAdmin />

        <UpdatesAdmin />

        <div data-testid="admin-restore-section" className="mb-10 bg-card rounded-2xl p-5">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-2"><LifeBuoy className="w-5 h-5 text-primary" /> Data-herstel</h2>
          <p className="text-sm text-muted-foreground mb-4">Zet het verslag, de reacties, foto's en het budget van <strong>'Road to Vero 60'</strong> eenmalig terug (gereconstrueerd uit je screenshots). Veilig om vaker te klikken — bestaande data wordt nooit overschreven.</p>
          <button data-testid="restore-vero-button" onClick={restoreVero} disabled={restoring}
            className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-5 py-2.5 text-xs font-bold disabled:opacity-50 transition-[background-color] duration-200">
            {restoring ? "Bezig met herstellen..." : "Herstel Vero 60-data"}
          </button>
        </div>

        <div className="relative mb-5 max-w-md">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam of e-mail..." className="h-11 rounded-xl pl-11" />
        </div>

        <div data-testid="admin-users-table" className="space-y-3">
          {users?.map((u) => (
            <div key={u.id} data-testid={`admin-user-row-${u.email}`}
              className={`bg-card rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${u.blocked ? "opacity-60" : ""}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm flex items-center gap-2">
                  {u.name || "—"}
                  {u.role === "admin" && <ShieldCheck className="w-4 h-4 text-primary" title="Admin" />}
                  {u.blocked && <span className="text-[10px] font-bold bg-red-100 text-red-600 rounded-full px-2 py-0.5">GEBLOKKEERD</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{u.email} · <span className="font-mono">{u.trip_count}</span> trips · sinds {u.created_at?.slice(0, 10)} · {u.last_login_at ? <>laatste login <span className="font-mono">{u.last_login_at.slice(0, 16).replace("T", " ")}</span></> : "nog niet ingelogd"}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <select data-testid={`admin-plan-${u.email}`} value={u.plan} onChange={(e) => update(u, { plan: e.target.value })}
                  className="bg-secondary rounded-full px-3 py-1.5 text-xs font-semibold outline-none cursor-pointer">
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="family">Family</option>
                </select>
                <select data-testid={`admin-role-${u.email}`} value={u.role} onChange={(e) => update(u, { role: e.target.value })}
                  className="bg-secondary rounded-full px-3 py-1.5 text-xs font-semibold outline-none cursor-pointer">
                  <option value="user">Gebruiker</option>
                  <option value="admin">Admin</option>
                </select>
                <button data-testid={`admin-block-${u.email}`} onClick={() => update(u, { blocked: !u.blocked })}
                  className={`p-2 rounded-full transition-[background-color] duration-200 ${u.blocked ? "bg-amber-100 text-amber-600 hover:bg-amber-200" : "hover:bg-amber-50 text-amber-500"}`}
                  title={u.blocked ? "Deblokkeren" : "Blokkeren"}>
                  <Ban className="w-4 h-4" />
                </button>
                <button data-testid={`admin-delete-${u.email}`} onClick={() => remove(u)}
                  className="p-2 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200" title="Verwijderen">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {users?.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Geen gebruikers gevonden.</p>}
        </div>

        <div className="mt-10">
          <GuestsAdmin />
        </div>

        <p data-testid="admin-version" className="mt-10 pb-4 text-center text-[11px] font-mono text-muted-foreground/70">
          VoltRoute admin{version ? ` · versie ${version}` : ""}
        </p>
      </main>
    </div>
  );
}
